import { useMemo } from 'react';
import useCalendarDataSelf from '../../hooks/useCalendarDataSelf';
import { processCalendarData, addPredictions } from './processCalendarData';

const TAG = '[usePredictedCalendar]';

/**
 * usePredictedCalendar - Hook that returns calendar data with cycle predictions
 *
 * This hook uses useCalendarDataSelf to get the raw calendar data, then processes it
 * with cycle day calculations and extends predictions until the given end date.
 *
 * @param {string} endDate - End date in YYYY-MM-DD format to extend predictions to. Defaults to 2 weeks from current date.
 * @returns {{ calendarData: object, loading: boolean }} - Calendar data with predictions and loading state
 */
export default function usePredictedCalendar(endDate) {
  const { calendarData: rawCalendarData, loading } = useCalendarDataSelf();

  // Default to 2 weeks from current date if no endDate provided
  const defaultEndDate = useMemo(() => {
    if (endDate) return endDate;
    const twoWeeksFromNow = new Date();
    twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);
    return twoWeeksFromNow.toISOString().split('T')[0];
  }, [endDate]);

  // Process calendar data with predictions
  const calendarData = useMemo(() => {
    const processed = processCalendarData(rawCalendarData);
    const withPredictions = addPredictions(processed, defaultEndDate);
    return withPredictions;
  }, [rawCalendarData, defaultEndDate]);

  return { calendarData, loading };
}
